import { useEffect, useRef, useState } from 'react';

// "Is a newer build deployed than the one this tab is running?" Each build
// embeds its id (import.meta.env.VITE_APP_VERSION) and ships the same id in
// /version.json (web/shared/vite/app-version.ts). The tab re-reads that file
// now and then; when it differs, a newer build has been deployed.

/** Background re-check cadence. */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60_000;
/** No two checks closer together than this, whatever triggers them. */
export const VERSION_CHECK_MIN_GAP_MS = 60_000;

/** The deployed build's id, or undefined on any failure (offline, a non-2xx,
 * a body that is not our JSON — e.g. a host's index.html fallback). */
export async function fetchDeployedVersion(url = '/version.json'): Promise<string | undefined> {
  try {
    const res = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!res.ok) return undefined;
    const body: unknown = await res.json();
    const version =
      body && typeof body === 'object' ? (body as { version?: unknown }).version : undefined;
    return typeof version === 'string' && version !== '' ? version : undefined;
  } catch {
    return undefined;
  }
}

export interface NewVersionCheckOptions {
  /** The id this bundle was built with; no id → no checks. */
  currentVersion: string | undefined;
  /** False in dev and mock/demo builds: then nothing is ever fetched. */
  enabled: boolean;
  /** Changes on navigation (e.g. the router's pathname); each change is a check. */
  routeKey?: string;
  url?: string;
}

/**
 * Re-checks /version.json every 5 minutes (while the tab is visible), when the
 * window regains focus or visibility, and on route change — never more than
 * once a minute, and the minute starts at page load (this build was fresh
 * then). Returns the deployed id once it differs from `currentVersion`, after
 * which it stops checking. Failures are silent: the next trigger just tries
 * again.
 */
export function useNewVersionCheck({
  currentVersion,
  enabled,
  routeKey,
  url = '/version.json',
}: NewVersionCheckOptions): { latestVersion: string | undefined } {
  const [latestVersion, setLatestVersion] = useState<string | undefined>(undefined);
  const active = enabled && !!currentVersion && latestVersion === undefined;
  const lastCheck = useRef(Date.now());
  const inFlight = useRef(false);

  // Re-created each render so the listeners below always see current props.
  const check = useRef<() => void>(() => {});
  check.current = () => {
    if (!active || inFlight.current) return;
    const now = Date.now();
    if (now - lastCheck.current < VERSION_CHECK_MIN_GAP_MS) return;
    lastCheck.current = now;
    inFlight.current = true;
    void fetchDeployedVersion(url)
      .then((deployed) => {
        if (deployed !== undefined && deployed !== currentVersion) setLatestVersion(deployed);
      })
      .finally(() => {
        inFlight.current = false;
      });
  };

  useEffect(() => {
    if (!active) return;
    const visible = () => document.visibilityState !== 'hidden';
    const onTick = () => {
      if (visible()) check.current();
    };
    const onFocus = () => check.current();
    const onVisibility = () => {
      if (visible()) check.current();
    };
    const timer = setInterval(onTick, VERSION_CHECK_INTERVAL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active]);

  useEffect(() => {
    check.current();
  }, [routeKey]);

  return { latestVersion };
}
